/**
 * RLQ — Retail Lobby Queue routes.
 *
 * Surfaces (per Bill 2026-04-23 modernization brief):
 *
 *   /rlq                       → redirect to /rlq/queue
 *   /rlq/queue                 → LobbyQueue (FSR+ landing)
 *   /rlq/entry/internal        → LobbyEntryInternal (FSR+)
 *   /rlq/entry/external        → LobbyEntryExternal (Manager+)
 *   /rlq/entry/kiosk           → LobbyEntryKiosk    (Kiosk role OR ?kiosk=1)
 *   /rlq/management            → LobbyManagement    (Supervisor+)
 *   /rlq/claim/:sessionId      → ClaimAndStart      (transient splash)
 *
 * Legacy aliases preserved so deep links don't 404:
 *   /rlq/kiosk                 → /rlq/entry/kiosk
 */
import { Routes, Route, Navigate } from 'react-router';
import { SplatRedirect } from '@/components/common/SplatRedirect';

import ClaimAndStart from './ClaimAndStart';
import LobbyQueue from './queue/LobbyQueue';
import LobbyEntryInternal from './entry/LobbyEntryInternal';
import LobbyEntryExternal from './entry/LobbyEntryExternal';
import LobbyEntryKiosk from './entry/LobbyEntryKiosk';
import LobbyManagement from './management/LobbyManagement';
import { RoleGate } from './shared/RoleGate';

export function RlqRoutes(): JSX.Element {
  return (
    <Routes>
      <Route index element={<Navigate to="queue" replace />} />

      <Route path="queue" element={<LobbyQueue />} />

      <Route
        path="entry/internal"
        element={
          <RoleGate
            roles={[
              'Officer',
              'Supervisor',
              'Manager',
              'Executive',
              'Admin',
              'SuperAdmin',
            ]}
            bounceMessage="Internal entry is for branch staff."
          >
            <LobbyEntryInternal />
          </RoleGate>
        }
      />

      <Route
        path="entry/external"
        element={
          <RoleGate
            roles={['Manager', 'Executive', 'Admin', 'SuperAdmin']}
            bounceMessage="External arrivals view is for managers and admins."
          >
            <LobbyEntryExternal />
          </RoleGate>
        }
      />

      <Route
        path="entry/kiosk"
        element={
          <RoleGate
            roles={['Admin', 'SuperAdmin']}
            allowKioskQueryParam
            bounceMessage="Kiosk mode is for the lobby tablet."
          >
            <LobbyEntryKiosk />
          </RoleGate>
        }
      />

      <Route
        path="management"
        element={
          <RoleGate
            roles={['Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin']}
            bounceMessage="Manager view is for supervisors and above."
          >
            <LobbyManagement />
          </RoleGate>
        }
      />

      <Route path="claim/:sessionId" element={<ClaimAndStart />} />

      {/* Legacy alias — keeps the older `/rlq/kiosk` deep link alive. */}
      <Route path="kiosk" element={<Navigate to="../entry/kiosk" replace />} />

      <Route path="*" element={<SplatRedirect to="queue" />} />
    </Routes>
  );
}
